/**
 * Animation. CSS transitions and a simple Interval wrapper.
 */

import { CSSNames, StyleNames } from './helpers'
import Easing from './easing'

const requestAnimFrame = window.requestAnimationFrame
	|| window.webkitRequestAnimationFrame
	|| window.msRequestAnimationFrame
	|| function (fn) { setTimeout(fn, 1000 / 60) }

const transitionEndEvents = ['transitionend', 'webkitTransitionEnd', 'msTransitionEnd']

const transitionMap = window.WeakMap ? new WeakMap() : new Map()

// Animates a given DOM element using CSS transitions.
// `onDone` is called on the /next/ frame.
export function animate(obj, properties, { curve = 'linear', duration = 1000, delay = 0 } = {}, onDone) {
	// Save the old one, if available.
	let oldTransition = ''

	// Prepare the new transition style.
	let newTransition = []
	for (let key in properties) {
		key = CSSNames[key] || key
		newTransition.push(`${key} ${duration}ms ${curve} ${delay}ms`)
	}
	obj.style[StyleNames.transition] = newTransition.join(', ')

	// Clear the old timeout.
	if (transitionMap.has(obj)) {
		let timeout = transitionMap.get(obj)
		clearTimeout(timeout)
		transitionMap.delete(obj)
	}

	// New event!
	function done() {
		if (onDone) {
			requestAnimationFrame(onDone.bind(obj))
		}
		obj.style[StyleNames.transition] = oldTransition
		transitionMap.delete(obj)
	}

	let timeout = setTimeout(done, duration + delay)
	transitionMap.set(obj, timeout)

	// Apply the styles on the next.
	requestAnimationFrame(function () {
		for (let [key, value] of Object.entries(properties)) {
			key = StyleNames[key] || key
			obj.style[key] = value
		}
	})
}


// Interval class.
export class Interval {
	constructor(properties, fn) {
		if (!fn) {
			throw Error('No function given to Interval.')
		}

		const settings = properties || {}
		const autostart = settings.autostart !== undefined ? settings.autostart : true
		this.delay = settings.delay || 0
		this.duration = settings.duration || 1000
		this.fps = settings.fps || 60
		this.loop = settings.loop || false

		this.fn = fn

		if (autostart) {
			this.start()
		}
	}

	restart() {
		this.stop()
		this.start()
		return this
	}

	start() {
		if (this.interval) {
			return this
		}
		
		const startTime = Date.now() + this.delay
		const frameDuration = 1000 / this.fps
		const step = frameDuration / this.duration
		let position = 0

		this.interval = setInterval(() => {
			const now = Date.now()

			if (startTime > now) {
				return
			}

			position = Math.min(position + step, 1)
			this.fn.call(this, position)

			if (position >= 1) {
				if (this.loop) {
					this.restart()
				}
				else {
					this.stop()
				}
			}
		}, frameDuration)

		return this
	}

	stop() {
		this.interval = clearInterval(this.interval)
		return this
	}	
}